import { FetchResult } from '@apollo/client/link/core';
import { ApolloError } from '@apollo/client';
import { getMessageParamsFromError } from '~/content/server-errors';
import { type AlertContextType, useAlert } from '~/shared/contexts';
import { type MessageParams } from '~/core/types/types';
import { useSnackbar, type SnackbarProviderContext } from './useSnackbar';
import { useIntl } from 'react-intl';
import { MutationFunctionOptions } from '@apollo/client/react/types/types';

interface ExecMutationParams<Response, Variables> {
    mutation: (
        options?: MutationFunctionOptions<Response, Variables>
    ) => Promise<FetchResult<Response>>;
    mutationOptions: MutationFunctionOptions<Response, Variables>;
    alertContext: AlertContextType;
    snackbarContext: SnackbarProviderContext;
    locale: Locale;
    successMessage?: MessageParams;
    errorMessage?: MessageParams;
    notificationType?: 'alert' | 'snackbar' | 'none';
}

export const execMutation = async <Response, Variables>({
    mutation,
    mutationOptions,
    alertContext,
    snackbarContext,
    locale,
    successMessage,
    errorMessage,
    notificationType = 'snackbar'
}: ExecMutationParams<Response, Variables>): Promise<FetchResult<Response>> => {
    const { openAlert } = alertContext;
    const { enqueueSnackbar, closeSnackbar } = snackbarContext;
    const open = notificationType === 'alert' ? openAlert : enqueueSnackbar;

    const loadingSnackbarKey =
        notificationType === 'none'
            ? undefined
            : enqueueSnackbar({
                  variant: 'info',
                  persist: true,
                  messageLocaleId: 'common.loading-ellipsis'
              });

    try {
        const response = await mutation(mutationOptions);

        loadingSnackbarKey && closeSnackbar(loadingSnackbarKey);

        if (successMessage && loadingSnackbarKey) {
            open({ variant: 'success', ...successMessage });
        }

        return response;
    } catch (e) {
        loadingSnackbarKey && closeSnackbar(loadingSnackbarKey);
        const error = e as ApolloError;
        const errorMessageParams = {
            variant: 'error' as const,
            ...(errorMessage || getMessageParamsFromError(error, locale))
        };
        if (errorMessageParams.titleLocaleId === 'common.error.generic.title') {
            openAlert(errorMessageParams);
        } else {
            open(errorMessageParams);
        }

        return Promise.resolve({ errors: error.graphQLErrors });
    }
};

interface ExecMutationHookParams<Response, Variables> {
    mutation: (
        options?: Omit<MutationFunctionOptions<Response, Variables>, 'optimisticResponse'>
    ) => Promise<FetchResult<Response>>;
    mutationOptions: MutationFunctionOptions<Response, Variables>;
    successMessage?: MessageParams;
    errorMessage?: MessageParams;
    notificationType?: 'alert' | 'snackbar' | 'none';
}

export const useExecMutation = () => {
    const alertContext = useAlert();
    const snackbarContext = useSnackbar();
    const { locale } = useIntl();
    return async <Response, Variables>(
        params: ExecMutationHookParams<Response, Variables>
    ): Promise<FetchResult<Response>> => {
        return execMutation<Response, Variables>({
            ...params,
            alertContext,
            snackbarContext,
            locale
        });
    };
};

export type ExecMutation = ReturnType<typeof useExecMutation>;
